import React, { useState, useEffect, useCallback, useRef } from "react";
import Editor, { useMonaco } from "@monaco-editor/react";
import * as Babel from "@babel/standalone";
import { createRoot } from "react-dom/client";
import "./reactLiveCodeEditor.css";
import apiEndpoints from "../../../API/apiEndpoints";
import Split from "react-split";

// Custom debounce function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || "Unknown error" };
  }
  componentDidCatch(error, info) {
    console.error("Runtime error caught in ErrorBoundary:", error, info);
  }
  handleReset = () => {
    this.setState({ hasError: false, message: "" });
  };
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ color: "red", padding: "10px", background: "#fff0f0" }}>
          <strong>Runtime Error:</strong>
          <pre style={{ whiteSpace: "pre-wrap" }}>{this.state.message}</pre>
          <button onClick={this.handleReset} style={{ marginTop: "10px" }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function ReactLiveCodeEditor({ interviewId }) {
  // Monaco Editor & Theme Configuration
  const monaco = useMonaco();
  const [editorTheme, setEditorTheme] = useState("vs-dark");
  const [textColor, setTextColor] = useState("#ffffff");

  // File & Editor State
  const [models, setModels] = useState({});
  const [filesClick, setFilesClick] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const previewRef = useRef(null);

  // Files & Questions
  const [question_bank, setquestion_bank] = useState([]);
  const [errors, setErrors] = useState({});

  // Timer & Autosave System
  const [saveStatus, setSaveStatus] = useState("");

  // Console & Error Output
  const [console2, setConsole] = useState(false);
  

  // UI Layout State
  const [showFileExplorer, setShowFileExplorer] = useState(true);
  const [isMinimised, setMinimised] = useState(false);

  // EVA AI Assistant
  const [evaSpeaking, setEvaSpeaking] = useState(false);
  const [hintCount, setHintCount] = useState(0);
  const maxHints = 3; // Maximum allowed hints

  const boilerplateCode = "";
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [questionBank, setQuestionBank] = useState(
    {});

  const [isloaded, setLoaded] = useState(false);

  const extractReactCodingAnswer = (data) => {
    const reactSkill = data.skills_feedback.find(
      (skill) => skill.skill === "React Coding"
    );

    if (reactSkill) {
      return reactSkill.questions[0].answer;
    }
    return null;
  };

  const fetchData = async (id) => {
    console.log("i am clicked")
    try {
      const result = await fetch(
        `${process.env.REACT_APP_BASE_URL}/${apiEndpoints.GET_REPORT_CARD}/${id}`,
        {
          method: 'GET',
          headers: {
              'Content-Type': 'application/json',
              // 'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiNjczM2I5Y2UzZmE3NjFhYzIyNTc0ZjQ3IiwidXNlcl9uYW1lIjoiSGFyc2giLCJvcmdfaWRzIjpbIjY3MmI5MjQzYWMwOTU4NmFmYTI2NGRjNCJdLCJleHAiOjE3NTA0OTAyNDd9.b8kcNZSveb2XmCHtgh11E8nrBb2QU36CDQWWJ4XVscM`
          }
      });
      const response = await result.json();

      if (result?.status) {
        const reactAnswer = extractReactCodingAnswer(response);
        if (reactAnswer) {
          try {
            const parsedAnswer = JSON.parse(reactAnswer);
            console.log("API Response:", parsedAnswer);
            
            // Set files directly from API response
            setFiles(parsedAnswer);
            setQuestionBank(reactAnswer);
            
            // Set App.js as current file if it exists
            if (parsedAnswer["App.js"]) {
              setCurrentFile("App.js");
              // Force a re-render of the editor with new content
              setTimeout(() => {
                compileAndRun("App.js", true);
              }, 100);
            }
            
            setLoaded(true);
          } catch (parseError) {
            console.error("Error parsing API response:", parseError);
          }
        }
      } else {
        console.log(`Unexpected response structure:${response?.detail}`, "error");
      }
    } catch (error) {
      console.error("Error fetching organization data:", error);
    }
  };
  
  // if (!isloaded) return null;
  const appJs = "";

  // Initialize defaultFiles with boilerplateCode["App.js"] if available
  const defaultFiles = {
    "App.js": boilerplateCode && boilerplateCode["App.js"] ? boilerplateCode["App.js"] : appJs,
  };

  const getLocalStorageKey = (type) =>
    `reactEditor_${type}_${interviewId}_${currentQuestionId}`;
  const [files, setFiles] = useState(() => {
    const saved = localStorage.getItem(getLocalStorageKey("editor-files"));
    return saved ? JSON.parse(saved) : defaultFiles;
  });
  const [currentFile, setCurrentFile] = useState(() => {
    return localStorage.getItem("editor-currentFile") || "App.js";
  });
  const [logs, setLogs] = useState(() => {
    const saved = localStorage.getItem("editor-logs");
    return saved ? JSON.parse(saved) : [];
  });
  const [submittedQuestions, setSubmittedQuestions] = useState({});

  // Create debounced handleSave
  const debouncedHandleSave = useCallback(
    debounce(() => {
      if (isDirty) {
        handleSave();
      }
    }, 500),
    [isDirty]
  );

  const theme = {
    dark: {
      background: "#1e1e1e",
      surface: "#252526",
      border: "#404040",
      text: "#ffffff",
      textSecondary: "#888888",
      primary: "#4CAF50",
      secondary: "#2d2d2d",
      editor: "vs-dark",
      fileExplorer: "#1e1e1e",
      console: "#1e1e1e",
      accent: "#4CAF50",
      hover: "#2d2d2d",
      activeTab: "#1e1e1e",
      inactiveTab: "#252526",
    },
    light: {
      background: "#ffffff",
      surface: "#f5f5f5",
      border: "#e0e0e0",
      text: "#333333",
      textSecondary: "#666666",
      primary: "#2196F3",
      secondary: "#eeeeee",
      editor: "light",
      fileExplorer: "#f8f8f8",
      console: "#f8f8f8",
      accent: "#2196F3",
      hover: "#e8e8e8",
      activeTab: "#ffffff",
      inactiveTab: "#f5f5f5",
    },
  };
  const currentTheme = editorTheme === "vs-dark" ? theme.dark : theme.light;
  const addLog = (message, color = "inherit") => {
    setLogs((prev) => {
      if (prev.length > 0 && prev[prev.length - 1].message === message) {
        return prev;
      }
      return [
        ...prev,
        { message, timestamp: new Date().toLocaleTimeString(), color },
      ];
    });
  };
  const compileAndRun = (file, isMainApp) => {
    try {
      const compiledModules = {};
      const scopedConsole = {
        log: (...args) => addLog(args.join(" ")),
        error: (...args) => addLog("Error: " + args.join(" ")),
      };

      // First compile all files
      for (const [filename, code] of Object.entries(files)) {
        try {
          const instrumentedCode = code
            .replace(/console\.log/g, "scopedConsole.log")
            .replace(/console\.error/g, "scopedConsole.error");
          const compiled = Babel.transform(instrumentedCode, {
            presets: ["react", "env"],
          }).code;
          compiledModules[filename] = {
            code: compiled,
            exports: {},
            evaluated: false,
          };
        } catch (err) {
          console.error(`Error compiling ${filename}:`, err);
          setErrors((prev) => ({ ...prev, [filename]: err.message }));
        }
      }

      const customRequire = (modName) => {
        if (modName === "react") return React;
        if (modName.startsWith("./")) {
          const target = modName.slice(2);
          const mod = compiledModules[target];
          if (!mod) {
            throw new Error(`Module not found: ${modName}`);
          }
          if (!mod.evaluated) {
            try {
              const module = { exports: {} };
              const fn = new Function(
                "exports",
                "module",
                "require",
                "React",
                "scopedConsole",
                mod.code
              );
              fn(module.exports, module, customRequire, React, scopedConsole);
              mod.exports = module.exports;
              mod.evaluated = true;
            } catch (err) {
              console.error(`Error evaluating ${target}:`, err);
              throw err;
            }
          }
          return mod.exports;
        }
        throw new Error(`Cannot find module: ${modName}`);
      };

      // Then evaluate the main file
      const mod = compiledModules[file];
      if (!mod) {
        throw new Error(`File not found: ${file}`);
      }

      try {
        const module = { exports: {} };
        const fn = new Function(
          "exports",
          "module",
          "require",
          "React",
          "scopedConsole",
          mod.code
        );
        fn(module.exports, module, customRequire, React, scopedConsole);
        mod.exports = module.exports;
        mod.evaluated = true;

        if (isMainApp && previewRef.current) {
          const Component = mod.exports.default;
          if (!Component) {
            throw new Error(`No default export found in ${file}`);
          }
          previewRef.current.innerHTML = "";
          const root = createRoot(previewRef.current);
          root.render(
            <ErrorBoundary>
              <Component />
            </ErrorBoundary>
          );
        }
        setErrors((prev) => ({ ...prev, [file]: "" }));
      } catch (err) {
        console.error(`Error evaluating ${file}:`, err);
        if (previewRef.current && file === "App.js") {
          previewRef.current.innerHTML = `<div style="color: red; white-space: pre-wrap;">${err.message}</div>`;
        }
        setErrors((prev) => ({ ...prev, [file]: err.message }));
      }
    } catch (err) {
      console.error("Compilation error:", err);
      if (previewRef.current && file === "App.js") {
        previewRef.current.innerHTML = `<div style="color: red; white-space: pre-wrap;">${err.message}</div>`;
      }
      setErrors((prev) => ({ ...prev, [file]: err.message }));
    }
  };
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    action: null,
    fileName: null,
  });

  const isQuestionSubmitted =
    (currentQuestionId &&
      submittedQuestions[interviewId]?.includes(currentQuestionId)) ||
    localStorage.getItem(getLocalStorageKey("submitted")) === "true";
  const toggleConsole = useCallback(() => {
    setConsole((prev) => !prev);
  }, []);

  const SaveButton = () => (
    <button
      onClick={handleSave}
      disabled={!isDirty || isQuestionSubmitted}
      style={{
        padding: "8px 16px",
        backgroundColor:
          !isDirty || isQuestionSubmitted
            ? currentTheme.secondary
            : currentTheme.accent,
        color:
          !isDirty || isQuestionSubmitted ? currentTheme.textSecondary : "#fff",
        border: "none",
        borderRadius: "6px",
        cursor: !isDirty || isQuestionSubmitted ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        opacity: isQuestionSubmitted ? 0.5 : 1,
      }}
    >
      <span style={{ fontSize: "16px" }}>💾</span>
      Save
    </button>
  );

  const handleSave = () => {
    try {
      localStorage.setItem(
        getLocalStorageKey("editor-files"),
        JSON.stringify(files)
      );
      console.log(
        "Saved files to localStorage with key:",
        getLocalStorageKey("editor-files")
      );
      setIsDirty(false);
      setSaveStatus("Saved!");
      addLog("Changes saved successfully", "green");
      setTimeout(() => setSaveStatus(""), 2000);
    } catch (err) {
      addLog(`Error saving files: ${err.message}`, "salmon");
      setSaveStatus("Save Failed");
      setTimeout(() => setSaveStatus(""), 2000);
    }
  };

  const parseTimeToSeconds = (timeString) => {
    if (!timeString) return 0;
    try {
      const [minutes, seconds] = timeString
        .split(":")
        .map((num) => parseInt(num, 10));
      if (isNaN(minutes) || isNaN(seconds)) return 0;
      return minutes * 60 + seconds;
    } catch (error) {
      console.error("Error parsing time string:", error);
      return 0;
    }
  };

  const formatSecondsToTime = (totalSeconds) => {
    if (totalSeconds <= 0) return "00:00";
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    if (boilerplateCode && typeof boilerplateCode === "object") {
      const fileNames = Object.keys(boilerplateCode);
      const data = fileNames
        .filter((key) => key !== "App.js") // Exclude App.js from question_bank
        .map((key) => ({
          title: key,
          description: boilerplateCode[key],
        }));
      console.log("File names:", fileNames);
      console.log("Question bank data:", data);
      setquestion_bank(data);
    } else {
      console.log("Boiler Plate not found or invalid");
      setquestion_bank([]);
    }
  }, [boilerplateCode]);



  useEffect(() => {
    if (question_bank.length === 0) return;
    const question_bankFiles = question_bank.reduce((acc, item) => {
      const safeTitle = item.title.replace(/[<>:"/\\|?*\n]/g, "");
      const fileName = `${safeTitle || "question_bank"}`;
      if (files[fileName] && files[fileName].includes(item.description)) {
        return acc;
      }
      acc[fileName] = `/* ${item.title}\n\n${item.description}\n*/`;
      return acc;
    }, {});
    setFiles((prev) => {
      const updatedFiles = {
        ...prev,
        ...question_bankFiles,
      };
      localStorage.setItem(
        getLocalStorageKey("editor-files"),
        JSON.stringify(updatedFiles)
      );
      return updatedFiles;
    });
  }, [question_bank]);

  useEffect(() => {
    localStorage.setItem(
      getLocalStorageKey("editor-files"),
      JSON.stringify(files)
    );
  }, [files]);

  useEffect(() => {
    localStorage.setItem("editor-currentFile", currentFile);
  }, [currentFile]);

  useEffect(() => {
    localStorage.setItem("editor-logs", JSON.stringify(logs));
  }, [logs]);

  useEffect(() => {
    if (!monaco) return;
    const newModels = {};
    for (const [name, code] of Object.entries(files)) {
      const uri = monaco.Uri.parse(`file:///${name}`);
      let model = monaco.editor.getModel(uri);
      if (!model) {
        model = monaco.editor.createModel(code, "javascript", uri);
      } else {
        model.setValue(code); // Update existing model with new content
      }
      newModels[name] = model;
    }
    setModels(newModels);
  }, [monaco, files]);

  useEffect(() => {
    const model = models[currentFile];
    if (!model) return;
    const listener = model.onDidChangeContent(() => {
      let updatedCode = model.getValue();
      setFiles((prev) => ({
        ...prev,
        [currentFile]: updatedCode,
      }));
      setIsDirty(true);
      debouncedHandleSave();
    });
    return () => listener.dispose();
  }, [models, currentFile, debouncedHandleSave]);

  useEffect(() => {
    const timer = setTimeout(() => {
      compileAndRun("App.js", true);
    }, 500);
    return () => clearTimeout(timer);
  }, [files, currentFile]);

  useEffect(() => {
    if (currentFile !== "App.js") {
      compileAndRun(currentFile, false);
    }
  }, [currentFile, files]);

  useEffect(() => {
    const speakingInterval = setInterval(() => {
      setEvaSpeaking((prev) => !prev);
    }, 5000);
    return () => clearInterval(speakingInterval);
  }, []);

  // Load hint count from localStorage when question changes
  useEffect(() => {
    const savedHintCount = localStorage.getItem(
      getLocalStorageKey("hintCount")
    );
    if (savedHintCount) {
      setHintCount(parseInt(savedHintCount, 10));
    } else {
      setHintCount(0);
    }
  }, [currentQuestionId]);

  useEffect(() => {
    if (interviewId) {
      const savedForInterview = localStorage.getItem(
        getLocalStorageKey("submittedQuestions")
      );
      if (savedForInterview) {
        setSubmittedQuestions(JSON.parse(savedForInterview));
      } else {
        setSubmittedQuestions({});
      }

      // Also check the specific question submission flag
      const isSubmitted =
        localStorage.getItem(getLocalStorageKey("submitted")) === "true";
      if (isSubmitted && currentQuestionId) {
        setSubmittedQuestions((prev) => ({
          ...prev,
          [interviewId]: [...(prev[interviewId] || []), currentQuestionId],
        }));
      }
    }
  }, [interviewId, currentQuestionId]);

  // Save hint count to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(getLocalStorageKey("hintCount"), hintCount.toString());
  }, [hintCount]);

  useEffect(() => {
    if (Object.keys(questionBank).length > 0 && questionBank["App.js"]) {
      setFiles(prev => ({
        ...prev,
        "App.js": questionBank["App.js"]
      }));
      setCurrentFile("App.js");
      compileAndRun("App.js", true);
    }
  }, [questionBank]);

  useEffect(() => {
    const url = window.location.href;
    const pathSegments = url.split("/");
    const id = pathSegments[4];
    console.log("id:", id);
    if (id) {
      fetchData(id);
    }
  }, []);

  // Add useEffect to sync question_bank with files
  useEffect(() => {
    if (Object.keys(question_bank).length > 0) {
      setFiles(question_bank);
    }
  }, [question_bank]);

  // Handle file selection from question_bank
  const handleFileSelect = (file) => {
    setCurrentFile(file);
    // Set the file content in the editor
    if (questionBank[file]) {
      setFiles(prev => ({
        ...prev,
        [file]: questionBank[file]
      }));
      // If it's App.js, compile and run it
      if (file === "App.js") {
        compileAndRun("App.js", true);
      }
    }
  };

  return (
    <div
      style={{
        backgroundColor: currentTheme.secondary,
      }}
    >
      <style>
        {`
      .hidden-scrollbar {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .hidden-scrollbar::-webkit-scrollbar {
        display: none;
      }
      .wave {
        width: 4px;
        height: 6px;
        background-color: #3b82f6;
        margin: 0 2px;
        border-radius: 2px;
        animation: wave 1.2s ease-in-out infinite;
      }
      .wave:nth-child(2) { animation-delay: 0.2s; }
      .wave:nth-child(3) { animation-delay: 0.4s; }
      .disable-wave {
        width: 4px;
        height: 4px;
        background-color: #6b7280;
        margin: 0 2px;
        border-radius: 2px;
      }
      @keyframes flicker {
        0% { border-color: #3b82f6; }
        50% { border-color: #93c5fd; }
        100% { border-color: #3b82f6; }
      }
      .animate-flicker { animation: flicker 1.5s infinite; }
      .bot-circle {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        alignItems: center;
        position: relative;
      }
      .bot-circle::before {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        border: 1px solid transparent;
        animation: flicker 1.5s infinite;
      }
      .bot-speaking .bot-circle::before { border-color: #3b82f6; }
      @keyframes wave {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
      .wave {
        width: 4px;
        height: 10px;
        background-color: #3b82f6;
        margin: 0 2px;
        border-radius: 2px;
        animation: wave 1.2s ease-in-out infinite;
        display: inline-block;
      }
    `}
      </style>
      {/* header */}
      <div style={{ position: "fixed", width: "100%", zIndex: 100 }}>
        <div
          style={{
            padding: "12px 20px",
            backgroundColor: currentTheme.surface,
            borderBottom: `1px solid ${currentTheme.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 10,
            position: "relative",
            transition: "all 0.2s ease",
            boxShadow: `0 2px 4px ${
              editorTheme === "vs-dark" ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)"
            }`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <div style={{ display: "flex", gap: 10 }}>
              <label
                style={{ cursor: "no-drop", fontSize: 14 }}
                className="animate-pulse"
              >
                <span> 🔴</span>
              </label>
              <label
                onClick={() => setMinimised(true)}
                style={{ cursor: "pointer", fontSize: 14 }}
              >
                <span> 🟡</span>
              </label>
              <label style={{ cursor: "no-drop", fontSize: 14 }}>
                <span> 🟢</span>
              </label>
            </div>
            <button
              onClick={() => {
                setFilesClick(!filesClick);
              }}
              style={{
                marginLeft: 12,
                backgroundColor: "#3a3a3a",
                color: "white",
                fontSize: "0.875rem",
                fontWeight: 600,
                borderRadius: 4,
                padding: "4px 12px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {filesClick ? "📂" : "🗐"}
              <div
                style={{ display: "flex", gap: "4px", alignItems: "center" }}
              >
                <span>Files</span>
                <br />
                <span style={{ fontSize: 8, opacity: 0.8 }}>
                  Click to {filesClick ? "Close" : "Open"}
                </span>
              </div>
            </button>
            <h2
              style={{
                margin: "0",
                color: currentTheme.text,
                fontSize: "20px",
                fontWeight: "500",
              }}
            >
              React Live Editor
            </h2>
            
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
           
            

            <button
              onClick={() => {
                const newTheme =
                  editorTheme === "vs-dark" ? "light" : "vs-dark";
                setEditorTheme(newTheme);
                setTextColor(newTheme === "light" ? "#000000" : "#ffffff");
              }}
              style={{
                padding: "8px",
                backgroundColor: currentTheme.secondary,
                color: currentTheme.text,
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "36px",
                height: "36px",
              }}
            >
              {editorTheme === "vs-dark" ? "☀️" : "🌙"}
            </button>
          </div>
        </div>
      </div>
      {/* mainlayout */}
      <div
        className="layout"
        style={{
          height: console2 ? "75vh" : "100vh",
          paddingTop: "60px",
          width: "100%",
        }}
      >
        <Split
          direction="horizontal"
          style={{
            height: "100%",
            width: filesClick ? "calc(100% - 220px)" : "100%",
            display: "flex",
            transition: "width 0.4s ease",
            marginLeft: filesClick ? "220px" : "0",
          }}
          sizes={[15, 65, 50]}
          minSize={[200, 500, 280]}
          gutterSize={10}
          cursor="col-resize"
        >
          {/* Sidebar */}
          <div
            className="sidebar"
            style={{
              position: "fixed",
              top: "60px",
              left: 0,
              width: "220px",
              height: "calc(100% - 60px)",
              backgroundColor: currentTheme.secondary,
              padding: "1rem",
              borderRight: "1px solid #333",
              opacity: filesClick ? 1 : 0,
              zIndex: 20,
              transform: filesClick ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 0.3s ease-in-out, opacity 0.3s",
              pointerEvents: "auto",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ color: textColor }}>Files</h2>
            </div>
            <ul style={{ listStyle: "none", padding: 0, marginTop: "1rem" }}>
              {Object.keys(files).map((file) => (
                <li
                  key={file}
                  onClick={() => handleFileSelect(file)}
                  style={{
                    backgroundColor:
                      currentFile === file
                        ? currentTheme.surface
                        : "transparent",
                    color:
                      currentFile === file
                        ? currentTheme.text
                        : currentTheme.textSecondary,
                    padding: "0.5rem",
                    borderRadius: 4,
                    marginBottom: 4,
                    fontSize: 13,
                    cursor: "pointer",
                    opacity: 1,
                    transition: "all 0.2s ease",
                    border: `1px solid ${
                      currentFile === file ? currentTheme.border : "transparent"
                    }`,
                    "&:hover": {
                      backgroundColor: currentTheme.surface,
                      color: currentTheme.text,
                    },
                  }}
                >
                  {file}
                </li>
              ))}
            </ul>
          </div>

          {/* Editor */}
          <div
            className="editor hidden-scrollbar"
            style={{
              background: currentTheme.secondary,
              overflow: "hidden",
              top: 10,
              transition: "margin-left 0.2s ease",
              boxShadow: `0 2px 4px ${
                editorTheme === "vs-dark"
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(0,0,0,0.1)"
              }`,
            }}
          >
            {monaco && models[currentFile] && (
              <Editor
                theme={editorTheme}
                language="javascript"
                height="100%"
                path={currentFile}
                onMount={(editor) => {
                  const model = models[currentFile];
                  if (model) {
                    editor.setModel(model);
                    editor.revealPositionInCenter({ lineNumber: 1, column: 1 });
                  }
                  editor.updateOptions({ 
                    readOnly: true,
                    domReadOnly: true,
                    contextmenu: false,
                    quickSuggestions: false,
                    parameterHints: { enabled: false },
                    suggestOnTriggerCharacters: false,
                    formatOnPaste: false,
                    formatOnType: false,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    lineNumbers: "on",
                    renderLineHighlight: "all",
                    automaticLayout: true,
                    tabSize: 2,
                    padding: { top: 20, bottom: 20 }
                  });
                }}
                options={{
                  readOnly: true,
                  domReadOnly: true,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  wordWrap: "on",
                  lineNumbers: "on",
                  renderLineHighlight: "all",
                  automaticLayout: true,
                  tabSize: 2,
                  padding: { top: 20, bottom: 20 },
                  formatOnPaste: false,
                  formatOnType: false,
                  quickSuggestions: false,
                  suggestOnTriggerCharacters: false,
                  parameterHints: { enabled: false },
                  suggest: {
                    showKeywords: false,
                    showSnippets: false,
                    showClasses: false,
                    showFunctions: false,
                    showVariables: false,
                  },
                  contextmenu: false
                }}
              />
            )}
          </div>

          {/* Preview */}
          <div
            className="preview"
            style={{
              padding: "1rem",
              width: "100%",
              backgroundColor: currentTheme.surface,
              color: currentTheme.text,
              borderLeft: `1px solid ${currentTheme.border}`,
              overflowY: "auto",
              boxShadow: `0 2px 4px ${
                editorTheme === "vs-dark"
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(0,0,0,0.1)"
              }`,
            }}
          >
            <h3
              style={{
                textAlign: "center",
                fontSize: "16px",
                padding: "12px 0",
                letterSpacing: "0.5px",
                color: currentTheme.text,
                borderBottom: `1px solid ${currentTheme.border}`,
                marginBottom: "1rem",
                fontWeight: "500",
              }}
            >
              🔍 Output: App.js
            </h3>

            <div
              ref={previewRef}
              style={{
                marginTop: "5px",
                color: currentTheme.text,
                padding: "1rem",
                backgroundColor: currentTheme.background,
                borderRadius: "8px",
                minHeight: "200px",
                display: "flex",
                heihgt: "100%",
              }}
            />

            {errors["App.js"] && (
              <div
                style={{
                  background: editorTheme === "vs-dark" ? "#3a1f1f" : "#fee2e2",
                  color: editorTheme === "vs-dark" ? "#fecaca" : "#991b1b",
                  padding: "1rem",
                  borderRadius: "8px",
                  marginTop: "1rem",
                  border: `1px solid ${
                    editorTheme === "vs-dark" ? "#7f1d1d" : "#fecaca"
                  }`,
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                    fontWeight: "500",
                  }}
                >
                  <span>⚠️</span>
                  <span>Error</span>
                </div>
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                  }}
                >
                  {errors["App.js"]}
                </pre>
              </div>
            )}
          </div>
        </Split>
      </div>

      {/* Console */}
      <div
        style={{
          transition: "all 0.3s ease",
          boxShadow: `0 2px 4px ${
            editorTheme === "vs-dark" ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)"
          }`,
        }}
      >
        {console2 ? (
          <div
            className="hidden-scrollbar"
            style={{
              position: "fixed",
              bottom: console2 ? 0 : "-25vh",
              left: filesClick ? "220px" : 0,
              right: 0,
              backgroundColor: currentTheme.secondary,
              color: textColor,
              padding: "1rem",
              borderTop: "none",
              height: "25vh",
              overflowY: "auto",
              transition: "all 0.3s ease-in-out",
              boxShadow: `0 2px 4px ${
                editorTheme === "vs-dark"
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(0,0,0,0.1)"
              }`,
            }}
          >
            <h3 style={{ color: textColor, marginBottom: "0.5rem" }}>
              Console
            </h3>
            <div style={{ fontSize: "0.85rem" }}>
              {logs.map((log, i) => {
                let color = log.color || textColor;

                // Auto-detect color based on log content
                const msg = log.message.toLowerCase();
                if (msg.includes("created and saved")) {
                  color = "green";
                } else if (msg.includes("deleted")) {
                  color = "red";
                } else if (msg.includes("changes saved successfully")) {
                  color = "yellow";
                }

                return (
                  <div
                    key={i}
                    style={{
                      marginBottom: "0.5rem",
                      color,
                    }}
                  >
                    <span
                      style={{
                        color: textColor,
                        marginRight: "0.5rem",
                        fontSize: 17,
                      }}
                    >
                      [ {log.timestamp} ]
                    </span>
                    <span style={{ fontSize: 17 }}>{log.message}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          ""
        )}
      </div>

     
    </div>
  );
}
